<script>
import { useAlert } from 'dashboard/composables';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import ZakiAgentSettingAPI from 'dashboard/api/zakiAgentSetting';

export default {
  components: {
    SettingsLayout,
    BaseSettingsHeader,
    Switch,
  },
  data() {
    return {
      isLoading: true,
      isSaving: false,
      enabled: false,
      autoSend: false,
      agentConfigured: false,
      agentName: '',
      agentEmail: '',
    };
  },
  mounted() {
    this.fetchSettings();
  },
  methods: {
    async fetchSettings() {
      this.isLoading = true;
      try {
        const { data } = await ZakiAgentSettingAPI.get();
        this.applyPayload(data.payload);
      } catch (error) {
        useAlert(this.$t('ZAKI_AGENT.FETCH_ERROR'));
      } finally {
        this.isLoading = false;
      }
    },
    applyPayload(payload) {
      this.enabled = !!payload.enabled;
      this.autoSend = !!payload.auto_send;
      this.agentConfigured = !!payload.agent_configured;
      this.agentName = payload.agent_name || '';
      this.agentEmail = payload.agent_email || '';
    },
    async updateSetting(changes) {
      this.isSaving = true;
      try {
        const { data } = await ZakiAgentSettingAPI.update(changes);
        this.applyPayload(data.payload);
        useAlert(this.$t('ZAKI_AGENT.SAVED'));
      } catch (error) {
        // Revert the optimistic toggle on failure by re-fetching real state.
        this.fetchSettings();
        useAlert(this.$t('ZAKI_AGENT.SAVE_ERROR'));
      } finally {
        this.isSaving = false;
      }
    },
    onToggleEnabled(value) {
      this.enabled = value;
      this.updateSetting({ enabled: value });
    },
    onToggleAutoSend(value) {
      this.autoSend = value;
      this.updateSetting({ auto_send: value });
    },
  },
};
</script>

<template>
  <SettingsLayout :is-loading="isLoading" :loading-message="$t('ZAKI_AGENT.LOADING')">
    <template #header>
      <BaseSettingsHeader
        :title="$t('ZAKI_AGENT.TITLE')"
        :description="$t('ZAKI_AGENT.DESCRIPTION')"
      />
    </template>

    <template #body>
      <div class="max-w-2xl">
        <div
          v-if="!agentConfigured"
          class="mb-6 p-4 rounded-lg bg-n-amber-3 text-n-amber-11 text-sm"
        >
          {{ $t('ZAKI_AGENT.NOT_CONFIGURED') }}
        </div>
        <div v-else class="mb-6 p-4 rounded-lg bg-n-slate-3 text-n-slate-11 text-sm">
          {{ $t('ZAKI_AGENT.CONFIGURED_AS', { name: agentName, email: agentEmail }) }}
        </div>

        <div class="flex items-start justify-between gap-4 py-4 border-b border-n-weak">
          <div>
            <p class="text-n-slate-12 font-medium mb-1">
              {{ $t('ZAKI_AGENT.ENABLED_LABEL') }}
            </p>
            <p class="text-n-slate-11 text-sm">
              {{ $t('ZAKI_AGENT.ENABLED_DESCRIPTION') }}
            </p>
          </div>
          <Switch
            :model-value="enabled"
            :disabled="isSaving"
            @change="onToggleEnabled"
          />
        </div>

        <div class="flex items-start justify-between gap-4 py-4 border-b border-n-weak">
          <div>
            <p class="text-n-slate-12 font-medium mb-1">
              {{ $t('ZAKI_AGENT.AUTO_SEND_LABEL') }}
            </p>
            <p class="text-n-slate-11 text-sm">
              {{ $t('ZAKI_AGENT.AUTO_SEND_DESCRIPTION') }}
            </p>
          </div>
          <Switch
            :model-value="autoSend"
            :disabled="isSaving || !enabled"
            @change="onToggleAutoSend"
          />
        </div>

        <p class="text-n-slate-11 text-sm mt-6">
          {{ $t('ZAKI_AGENT.ASSIGN_HINT') }}
        </p>
      </div>
    </template>
  </SettingsLayout>
</template>
